import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, KeyRound, Loader2, Send, XCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import DiscordIdentity from "@/components/DiscordIdentity";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function AwaitingPayRow({ t, onDone }) {
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/payments/${t.session_id}/${action}`);
      toast.success(action === "approve" ? "Payment approved — order moved to AWAITING KEY" : "Payment rejected");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
    setBusy(false);
  };
  return (
    <div className="grid grid-cols-[1.2fr_0.6fr_0.6fr_auto] gap-3 items-center px-5 py-3.5 border-b border-white/[0.06] min-w-[560px]"
      data-testid={`awaiting-pay-${t.session_id}`}>
      <span className="text-sm truncate">{t.product_name} <span className="font-mono text-[10px] text-white/40">{t.tier}{(t.quantity || 1) > 1 ? ` ×${t.quantity}` : ""}</span><br />
        <span className="font-mono text-[10px] text-white/35">{fmtDate(t.created_at)}</span>
        <DiscordIdentity discord={t.discord} email={t.email} testId={`pay-discord-${t.session_id}`} />
      </span>
      <span className="font-display text-lg">${(t.amount || 0).toFixed(2)}</span>
      <span className="font-mono text-xs">
        <span className="rounded-full border border-white/20 px-2 py-0.5 tracking-[0.1em]">{t.ref_code}</span>
        <span className="block mt-1 text-[9px] tracking-[0.15em] text-white/50" data-testid={`pay-method-${t.session_id}`}>{t.method_label || "MANUAL"}</span>
        {t.buyer_confirmed && <span className="block mt-1 text-[9px] tracking-[0.15em] text-white">SAYS PAID</span>}
      </span>
      <span className="flex items-center gap-2">
        <button onClick={() => act("approve")} disabled={busy} data-testid={`approve-pay-${t.session_id}`}
          className="btn-inj px-4 py-2 text-[10px] inline-flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} APPROVE
        </button>
        <button onClick={() => act("reject")} disabled={busy} data-testid={`reject-pay-${t.session_id}`}
          className="px-4 py-2 text-[10px] font-mono tracking-[0.1em] border border-white/15 text-white/50 hover:text-red-400 hover:border-red-500/50 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
          <XCircle size={12} /> REJECT
        </button>
      </span>
    </div>
  );
}

function PendingKeyRow({ o, onDone }) {
  const [inputs, setInputs] = useState({});
  const [emailInput, setEmailInput] = useState("");
  const [busy, setBusy] = useState(null);

  const deliver = async (key, itemIndex) => {
    const needsEmail = !o.buyer_email;
    if (needsEmail && !/^\S+@\S+\.\S+$/.test(emailInput.trim())) {
      toast.error("Add the buyer email first — the key email needs somewhere to go");
      return;
    }
    setBusy(itemIndex);
    try {
      const payload = { ...(key ? { key } : {}), item_index: itemIndex };
      if (needsEmail) payload.buyer_email = emailInput.trim();
      const { data } = await api.post(`/admin/orders/${o.session_id}/deliver-key`, payload);
      toast.success(
        data.delivery_channel === "discord" ? "Key delivered via Discord DM" :
        data.dm_error ? "Discord DM unavailable — key emailed to buyer instead" :
        data.email_sent ? "Key delivered + emailed to buyer" : data.email_queued ? "Key delivered — email queued for retry" : "Key delivered (email send unavailable)"
      );
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delivery failed");
    }
    setBusy(null);
  };

  const items = (o.items && o.items.length)
    ? o.items
    : [{ product_name: o.product_name, tier: o.tier, quantity: o.quantity, keys: o.keys || [] }];

  return (
    <div className="px-5 py-3.5 border-b border-white/[0.06] min-w-[680px]" data-testid={`pending-order-${o.session_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <span className="font-mono text-[10px] tracking-[0.15em] text-white/35">
          {fmtDate(o.created_at)} · ${(o.amount || 0).toFixed(2)} · <DiscordIdentity discord={o.discord} email={o.buyer_email || "email needed"} testId={`order-discord-${o.session_id}`} />
          <span className="ml-2 rounded-full border border-white/15 px-2 py-0.5 text-white/55" data-testid={`order-method-${o.session_id}`}>{o.method_label || "UNKNOWN"}</span>
        </span>
        {!o.buyer_email && (
          <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} type="email" placeholder="BUYER EMAIL TO SEND KEY"
            data-testid={`order-email-input-${o.session_id}`}
            className="w-64 bg-transparent border border-amber-300/30 px-3 py-2 font-mono text-[11px] focus:border-amber-200 focus:outline-none placeholder:text-white/25" />
        )}
      </div>
      {items.map((it, idx) => {
        const sent = (it.keys || []).length;
        const need = it.quantity || 1;
        const done = sent >= need;
        return (
          <div key={idx} className="grid grid-cols-[1.2fr_auto] gap-3 items-center py-2 border-t border-white/[0.06] first:border-t-0" data-testid={`pending-item-${o.session_id}-${idx}`}>
            <span className="text-sm truncate">
              {it.product_name} <span className="font-mono text-[10px] text-white/40">{it.tier}{need > 1 ? ` ×${need} (${sent}/${need} sent)` : ""}</span>
              {done && <span className="ml-2 font-mono text-[9px] tracking-[0.15em] text-white/50">✓ SENT</span>}
            </span>
            {!done && (
              <span className="flex items-center gap-2">
                <button onClick={() => deliver(null, idx)} disabled={busy !== null} data-testid={`deliver-auto-${o.session_id}-${idx}`}
                  className="btn-ghost px-3 py-2 text-[10px] inline-flex items-center gap-1.5 disabled:opacity-60" title="Pull a key from the pool and deliver it (Discord DM first, email fallback)">
                  {busy === idx ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} FROM POOL
                </button>
                <input value={inputs[idx] || ""} onChange={(e) => setInputs({ ...inputs, [idx]: e.target.value })} placeholder="or paste key"
                  data-testid={`deliver-key-input-${o.session_id}-${idx}`}
                  className="w-32 bg-transparent border border-white/15 px-2 py-2 font-mono text-[11px] focus:border-white focus:outline-none placeholder:text-white/25" />
                <button onClick={() => (inputs[idx] || "").trim() && deliver(inputs[idx].trim(), idx)} disabled={busy !== null || !(inputs[idx] || "").trim()}
                  data-testid={`deliver-key-submit-${o.session_id}-${idx}`}
                  className="btn-inj px-3 py-2 text-[10px] inline-flex items-center gap-1.5 disabled:opacity-60">
                  <Send size={12} /> SEND
                </button>
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EmailHealthPanel({ health, onResend, resending }) {
  const counts = health?.counts || { queued: 0, sent: 0, failed: 0 };
  const jobs = health?.jobs || [];
  const cards = [
    ["queued", "QUEUED", "text-amber-200 border-amber-300/30"],
    ["sent", "SENT", "text-emerald-200 border-emerald-300/30"],
    ["failed", "FAILED", "text-red-300 border-red-400/35"],
  ];
  return (
    <section className="rounded-2xl overflow-hidden border border-white/10" data-testid="email-health-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
        <div>
          <div className="font-mono text-xs tracking-[0.2em] text-white">// KEY EMAIL HEALTH</div>
          <div className="mt-1 font-mono text-[9px] tracking-[0.14em] text-white/35">QUEUE · DELIVERY · FAILURE VISIBILITY</div>
        </div>
        <div className="grid grid-cols-3 gap-px rounded-xl overflow-hidden border border-white/10 bg-white/10">
          {cards.map(([key, label, cls]) => (
            <div key={key} className={`bg-black px-4 py-2 text-center border ${cls}`} data-testid={`email-health-${key}`}>
              <div className="font-display text-xl leading-none">{counts[key] || 0}</div>
              <div className="mt-1 font-mono text-[8px] tracking-[0.18em]">{label}</div>
            </div>
          ))}
        </div>
      </div>
      {jobs.length === 0 ? (
        <div className="px-5 py-6 font-mono text-[10px] tracking-[0.18em] text-white/35">// NO KEY EMAIL EVENTS RECORDED YET</div>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          {jobs.slice(0, 12).map((job) => (
            <div key={job.id} className="grid gap-3 border-b border-white/[0.06] px-5 py-3 last:border-b-0 md:grid-cols-[1fr_0.7fr_auto] md:items-center"
              data-testid={`email-job-${job.session_id}-${job.item_index}`}>
              <div className="min-w-0">
                <div className="truncate text-sm">{job.product_name || "ORDER EMAIL"} <span className="font-mono text-[10px] text-white/35">{job.tier || ""}</span></div>
                <div className="mt-1 truncate font-mono text-[10px] tracking-[0.1em] text-white/45">{job.channel === "discord" ? "DISCORD DM" : (job.buyer_email || "NO EMAIL")} · ATTEMPTS {job.attempts || 0}</div>
                {job.last_error && <div className="mt-1 truncate font-mono text-[9px] text-red-300/70" title={job.last_error}>{job.last_error}</div>}
              </div>
              <div className={`font-mono text-[10px] tracking-[0.18em] ${job.status === "sent" ? "text-emerald-200" : job.status === "failed" ? "text-red-300" : "text-amber-200"}`}>
                {job.status === "pending" ? "QUEUED" : job.status.toUpperCase()}{job.channel === "discord" ? " · DM" : ""}
                <span className="block text-[8px] text-white/25">{fmtDate(job.updated_at || job.created_at)}</span>
              </div>
              <button onClick={() => onResend(job.session_id)} disabled={resending === job.session_id}
                data-testid={`email-job-retry-${job.session_id}-${job.item_index}`}
                className="btn-ghost px-3 py-2 text-[9px] disabled:opacity-50">
                {resending === job.session_id ? "SENDING..." : job.status === "sent" ? "RESEND" : "RETRY NOW"}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


export default function AdminTransactions() {
  const [txs, setTxs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState("");
  const [emailHealth, setEmailHealth] = useState(null);

  const load = () => {
    api.get("/admin/transactions").then((r) => setTxs(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/email-health").then((r) => setEmailHealth(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const resendEmail = async (sessionId) => {
    setResending(sessionId);
    try {
      const { data } = await api.post(`/admin/orders/${sessionId}/resend-email`);
      toast.success(data.queued ? `Email queued for retry to ${data.buyer_email}` : `Key email resent to ${data.buyer_email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
    }
    setResending("");
    load();
  };

  const pending = orders.filter((o) => ["pending", "partial"].includes(o.key_status) && (o.keys || []).length < (o.quantity || 1));
  const awaitingPay = txs.filter((t) => t.provider === "manual" && ["awaiting_payment", "awaiting_review"].includes(t.status));

  if (loading) return <div className="py-16 text-center font-mono text-xs tracking-[0.2em] text-white/40">// LOADING TRANSACTIONS...</div>;

  return (
    <div className="space-y-8">
      <EmailHealthPanel health={emailHealth} onResend={resendEmail} resending={resending} />

      {awaitingPay.length > 0 && (
        <div data-testid="awaiting-payment-section">
          <div className="mb-3 font-mono text-xs tracking-[0.2em] text-white">
            // AWAITING PAYMENT ({awaitingPay.length}) — CHECK YOUR VENMO/APPLE PAY, THEN APPROVE
          </div>
          <div className="rounded-2xl border border-white/25 overflow-x-auto" data-testid="admin-awaiting-payments">
            <div className="grid grid-cols-[1.2fr_0.6fr_0.6fr_auto] gap-3 px-5 py-4 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-white/40 min-w-[560px]">
              <span>ORDER / BUYER</span><span>AMOUNT</span><span>REF CODE</span><span>ACTION</span>
            </div>
            {awaitingPay.map((t) => <AwaitingPayRow key={t.session_id} t={t} onDone={load} />)}
          </div>
        </div>
      )}

      {pending.length > 0 && (
        <div data-testid="pending-keys-section">
          <div className="mb-3 font-mono text-xs tracking-[0.2em] text-red-300/90">
            // AWAITING KEY ({pending.length}) — BUYER PAID, SEND A KEY FROM THE POOL OR PASTE ONE
          </div>
          <div className="border border-red-500/25 overflow-x-auto" data-testid="admin-pending-orders">
            <div className="px-5 py-4 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-white/40 min-w-[680px]">
              <span>ORDER · BUYER EMAIL · PER-ITEM KEY DELIVERY</span>
            </div>
            {pending.map((o) => <PendingKeyRow key={o.session_id} o={o} onDone={load} />)}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 overflow-x-auto" data-testid="admin-transactions">
        <div className="grid grid-cols-[1.1fr_1.2fr_0.55fr_0.55fr_0.55fr_0.55fr_0.65fr_1fr] gap-3 px-5 py-4 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-white/40 min-w-[880px]">
          <span>PRODUCT</span><span>DISCORD BUYER</span><span>TIER</span><span>AMOUNT</span><span>METHOD</span><span>CODE</span><span>STATUS</span><span>DATE</span>
        </div>
        {txs.map((t) => {
          const linkedOrder = orders.find((o) => o.session_id === t.session_id);
          return (
          <div key={t.session_id} className="grid grid-cols-[1.1fr_1.2fr_0.55fr_0.55fr_0.55fr_0.55fr_0.65fr_1fr] gap-3 items-center px-5 py-3.5 border-b border-white/[0.06] min-w-[880px]">
            <span className="text-sm truncate">{t.product_name || t.slug}</span>
            <DiscordIdentity discord={t.discord} email={t.email} testId={`tx-discord-${t.session_id}`} />
            <span className="font-mono text-xs text-white/60">{t.tier}</span>
            <span className="font-display text-base">${(t.amount || 0).toFixed(2)}</span>
            <span className="font-mono text-[10px] tracking-[0.12em] text-white/65" data-testid={`tx-method-${t.session_id}`}>{t.method_label || (t.provider || "—").toUpperCase()}</span>
            <span className="font-mono text-xs text-white/50">{t.code || "—"}</span>
            <span className={`font-mono text-[10px] tracking-[0.15em] ${t.payment_status === "paid" ? "text-white" : "text-white/40"}`}>
              {(t.payment_status || "pending").toUpperCase()}
            </span>
            <span className="font-mono text-[10px] text-white/40">
              {fmtDate(t.created_at)}
              {linkedOrder?.key_status === "delivered" && (
                <button onClick={() => resendEmail(t.session_id)} disabled={resending === t.session_id}
                  data-testid={`resend-key-email-${t.session_id}`}
                  className="mt-2 block rounded-full border border-white/15 px-2 py-1 text-[9px] tracking-[0.14em] text-white/60 transition-colors hover:border-white/50 hover:text-white disabled:opacity-50">
                  {resending === t.session_id ? "SENDING..." : "RESEND KEY EMAIL"}
                </button>
              )}
            </span>
          </div>
          );
        })}
        {txs.length === 0 && <div className="px-5 py-12 text-center font-mono text-xs tracking-[0.2em] text-white/40">// NO TRANSACTIONS YET</div>}
      </div>
    </div>
  );
}
